import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FolderOpen, Plus, Pencil, Trash, Building, Users, FileText, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Projects = ({ user }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectStats, setProjectStats] = useState({});
  
  const [newProject, setNewProject] = useState({
    name: '',
    code: '',
    description: ''
  });

  const isSuperAdmin = user?.is_super_admin || user?.role?.toLowerCase() === 'admin';

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
      
      // Fetch stats for each project
      const statsPromises = response.data.map(p => 
        axios.get(`${API}/projects/${p.id}/stats`).catch(() => ({ data: {} }))
      );
      const statsResults = await Promise.all(statsPromises);
      const statsMap = {};
      response.data.forEach((p, i) => {
        statsMap[p.id] = statsResults[i].data;
      });
      setProjectStats(statsMap);
    } catch (error) {
      toast.error('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProject = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/projects`, newProject);
      toast.success('Project created successfully');
      setAddDialogOpen(false);
      setNewProject({ name: '', code: '', description: '' });
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    }
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/projects/${selectedProject.id}`, {
        name: selectedProject.name,
        code: selectedProject.code,
        description: selectedProject.description,
        is_active: selectedProject.is_active
      });
      toast.success('Project updated successfully');
      setEditDialogOpen(false);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update project');
    }
  };

  const handleDeleteProject = async () => {
    try {
      await axios.delete(`${API}/projects/${selectedProject.id}`);
      toast.success('Project deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedProject(null);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  const openEditDialog = (project) => {
    setSelectedProject({ ...project });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (project) => {
    setSelectedProject(project);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-xl">
              <Building className="h-6 w-6 text-indigo-600" />
            </div>
            Projects
          </h1>
          <p className="text-gray-500 mt-1">Manage projects and their data segregation</p>
        </div>
        {isSuperAdmin && (
          <Button 
            onClick={() => setAddDialogOpen(true)} 
            className="bg-blue-900 hover:bg-blue-800 rounded-xl h-10"
            data-testid="add-project-btn"
          >
            <Plus size={16} className="mr-2" />
            Add Project
          </Button>
        )}
      </div>

      {/* Projects Grid */}
      {projects.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <Building size={48} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 mb-2">No projects yet</p>
          <p className="text-sm text-gray-400 mb-4">Create a project to start organizing your documents</p>
          {isSuperAdmin && (
            <Button onClick={() => setAddDialogOpen(true)} className="bg-blue-900 hover:bg-blue-800">
              <Plus size={16} className="mr-2" />
              Create First Project
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => {
            const stats = projectStats[project.id] || {};
            return (
              <Card key={project.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{project.name}</CardTitle>
                      <span className="text-sm font-mono text-gray-500">{project.code}</span>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${project.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {project.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  {project.description && (
                    <p className="text-sm text-gray-600 mb-4">{project.description}</p>
                  )}
                  
                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <FileText size={16} className="text-blue-500" />
                      <span className="text-gray-600">{stats.documents || 0} Documents</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <BarChart3 size={16} className="text-purple-500" />
                      <span className="text-gray-600">{stats.review_cycles || 0} Cycles</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Users size={16} className="text-green-500" />
                      <span className="text-gray-600">{stats.staff_users || 0} Staff</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Users size={16} className="text-orange-500" />
                      <span className="text-gray-600">{stats.portal_users || 0} Portal</span>
                    </div>
                  </div>

                  {/* Actions */}
                  {isSuperAdmin && (
                    <div className="flex gap-2 pt-3 border-t">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => openEditDialog(project)}
                      >
                        <Pencil size={14} className="mr-1" />
                        Edit
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => openDeleteDialog(project)}
                      >
                        <Trash size={14} />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Project Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-md rounded-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building size={20} />
              Create New Project
            </DialogTitle>
            <DialogDescription>
              Projects allow you to separate documents and users by different work scopes.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddProject} className="space-y-4">
            <div>
              <Label className="text-sm">Project Name*</Label>
              <Input
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                placeholder="e.g., Tower Construction Phase 1"
                className="h-10 rounded-lg"
                required
                data-testid="new-project-name"
              />
            </div>
            <div>
              <Label className="text-sm">Project Code*</Label>
              <Input
                value={newProject.code}
                onChange={(e) => setNewProject({ ...newProject, code: e.target.value.toUpperCase() })}
                placeholder="e.g., TCP-1"
                className="h-10 rounded-lg font-mono"
                required
                data-testid="new-project-code"
              />
              <p className="text-xs text-gray-500 mt-1">Short unique identifier for the project</p>
            </div>
            <div>
              <Label className="text-sm">Description</Label>
              <Input
                value={newProject.description}
                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                placeholder="Brief description of the project..."
                className="h-10 rounded-lg"
                data-testid="new-project-description"
              />
            </div>
            <Button type="submit" className="w-full h-10 rounded-lg bg-blue-900 hover:bg-blue-800" data-testid="create-project-btn">
              Create Project
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md rounded-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil size={18} />
              Edit Project
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateProject} className="space-y-4">
            <div>
              <Label className="text-sm">Project Name*</Label>
              <Input
                value={selectedProject?.name || ''}
                onChange={(e) => setSelectedProject({ ...selectedProject, name: e.target.value })}
                className="h-10 rounded-lg"
                required
              />
            </div>
            <div>
              <Label className="text-sm">Project Code*</Label>
              <Input
                value={selectedProject?.code || ''}
                onChange={(e) => setSelectedProject({ ...selectedProject, code: e.target.value.toUpperCase() })}
                className="h-10 rounded-lg font-mono"
                required
              />
            </div>
            <div>
              <Label className="text-sm">Description</Label>
              <Input
                value={selectedProject?.description || ''}
                onChange={(e) => setSelectedProject({ ...selectedProject, description: e.target.value })}
                className="h-10 rounded-lg"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="project-active"
                checked={selectedProject?.is_active ?? true}
                onChange={(e) => setSelectedProject({ ...selectedProject, is_active: e.target.checked })}
                className="rounded"
              />
              <label htmlFor="project-active" className="text-sm">Active</label>
            </div>
            <Button type="submit" className="w-full h-10 rounded-lg bg-blue-900 hover:bg-blue-800">
              Save Changes
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{selectedProject?.name}&quot;? This action cannot be undone.
              <br /><br />
              <strong>Note:</strong> Projects with documents cannot be deleted. Deactivate them instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteProject}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Projects;
